import {
  type CSSProperties,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const SWIPE_THRESHOLD = 100;
const ROTATION_FACTOR = 0.05;
const EXIT_ANIMATION_DURATION_MS = 500;
const EXIT_TRANSLATE_MULTIPLIER = 1.5;

interface SwipeSource {
  fromTap?: boolean;
}

interface UseSwipeGestureProps {
  onSwipeLeft: (source?: SwipeSource) => void;
  onSwipeRight: (source?: SwipeSource) => void;
  isEnabled?: boolean;
  /** When this value changes, event listeners are re-attached to the current cardRef element. */
  cardKey?: string | number;
}

interface UseSwipeGestureReturn {
  cardRef: RefObject<HTMLDivElement | null>;
  style: CSSProperties;
  isAnimatingExit: boolean;
  deltaX: number;
  triggerSwipe: (direction: "left" | "right", source?: SwipeSource) => void;
}

export function useSwipeGesture(props: UseSwipeGestureProps): UseSwipeGestureReturn {
  const { onSwipeLeft, onSwipeRight, isEnabled = true, cardKey } = props;

  const cardRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isAnimatingExitRef = useRef(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Store callbacks in refs to avoid event listener churn
  const onSwipeLeftRef = useRef(onSwipeLeft);
  onSwipeLeftRef.current = onSwipeLeft;
  const onSwipeRightRef = useRef(onSwipeRight);
  onSwipeRightRef.current = onSwipeRight;
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;

  const [deltaX, setDeltaX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimatingExit, setIsAnimatingExit] = useState(false);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  const animateExit = useCallback((direction: "left" | "right", source?: SwipeSource) => {
    isAnimatingExitRef.current = true;
    setIsAnimatingExit(true);
    setDeltaX(
      direction === "right"
        ? window.innerWidth * EXIT_TRANSLATE_MULTIPLIER
        : -window.innerWidth * EXIT_TRANSLATE_MULTIPLIER
    );

    exitTimerRef.current = setTimeout(() => {
      if (direction === "right") {
        onSwipeRightRef.current(source);
      } else {
        onSwipeLeftRef.current(source);
      }

      isAnimatingExitRef.current = false;
      setIsAnimatingExit(false);
      setDeltaX(0);
    }, EXIT_ANIMATION_DURATION_MS);
  }, []);

  const triggerSwipe = useCallback(
    (direction: "left" | "right", source?: SwipeSource) => {
      if (!isEnabledRef.current || isAnimatingExitRef.current) {
        return;
      }

      animateExit(direction, source);
    },
    [animateExit]
  );

  const handlePointerDown = useCallback((event: PointerEvent) => {
    if (!isEnabledRef.current || isAnimatingExitRef.current) {
      return;
    }

    startXRef.current = event.clientX;
    isDraggingRef.current = true;
    setIsDragging(true);

    cardRef.current?.setPointerCapture(event.pointerId);
  }, []);

  const handlePointerMove = useCallback((event: PointerEvent) => {
    if (!isDraggingRef.current) {
      return;
    }

    setDeltaX(event.clientX - startXRef.current);
  }, []);

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (!isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = false;
      setIsDragging(false);
      cardRef.current?.releasePointerCapture(event.pointerId);

      const currentDelta = event.clientX - startXRef.current;

      if (Math.abs(currentDelta) > SWIPE_THRESHOLD) {
        animateExit(currentDelta > 0 ? "right" : "left");
      } else {
        setDeltaX(0);
      }
    },
    [animateExit]
  );

  const handlePointerCancel = useCallback((event: PointerEvent) => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    setIsDragging(false);
    cardRef.current?.releasePointerCapture(event.pointerId);
    setDeltaX(0);
  }, []);

  useEffect(() => {
    const element = cardRef.current;
    if (!element || !isEnabled) {
      return;
    }

    element.addEventListener("pointerdown", handlePointerDown);
    element.addEventListener("pointermove", handlePointerMove);
    element.addEventListener("pointerup", handlePointerUp);
    element.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      element.removeEventListener("pointerdown", handlePointerDown);
      element.removeEventListener("pointermove", handlePointerMove);
      element.removeEventListener("pointerup", handlePointerUp);
      element.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [
    isEnabled,
    cardKey,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  ]);

  const style: CSSProperties = {
    transform: `translateX(${deltaX}px) rotate(${deltaX * ROTATION_FACTOR}deg)`,
    transition: isDragging ? "none" : `transform ${EXIT_ANIMATION_DURATION_MS}ms ease-out`,
    touchAction: "none",
    cursor: isEnabled && !isAnimatingExit ? "grab" : "default",
    userSelect: "none",
    willChange: isDragging || isAnimatingExit ? "transform" : "auto",
  };

  return { cardRef, style, isAnimatingExit, deltaX, triggerSwipe };
}
